import {
  OpenUrlTarget,
  openURL,
} from '@eCommerce/frontend-legacy-shared-library';

export const maskEmail = (email: string) => {
  // assuming email has a valid format
  const parsedEmail = email.split('@');
  const masked = parsedEmail[0].split('').map((char, index) => {
    if (index > 1) {
      return '*';
    }
    return char;
  });
  return `${masked.join('')}@${parsedEmail[1]}`;
};

export const maskPhoneNumber = (phoneNumber: string) => {
  // assuming phone number has 10 digits
  const slicedNumber = phoneNumber.slice(6);
  return `*** *** ${slicedNumber}`;
};

export const formatPhoneNumber = (phoneNumber: string) => {
  if (phoneNumber.length === 10) {
    return `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(
      3,
      6,
    )}-${phoneNumber.substring(6)}`;
  }
  if (phoneNumber.length === 11) {
    return `${phoneNumber.substring(0, 1)}-${phoneNumber.substring(
      1,
      4,
    )}-${phoneNumber.substring(4, 7)}-${phoneNumber.substring(7)}`;
  }
  return phoneNumber;
};

export const isAbsoluteUrl = (url: string) => {
  const r = new RegExp('^(?:[a-z+]+:)?//', 'i');
  return r.test(url);
};

// This function is created to include the cetat=playbytr param in forward url before redirecting if necessary
export const redirectWithForwardUrl = (
  forwardUrl: string,
  target: OpenUrlTarget = OpenUrlTarget.SELF,
) => {
  const webViewKey = 'cetat';
  const webViewValue = 'playbytr';
  const searchParams = new URLSearchParams(window.location.search);
  const isWebView = searchParams.get(webViewKey) === webViewValue;
  let decodedUrl = isUrlEncoded(forwardUrl)
    ? decodeURIComponent(forwardUrl)
    : forwardUrl;
  // Add in playbytr
  if (isWebView) {
    decodedUrl = addQueryParam(decodedUrl, 'cetat', 'playbytr');
  }
  openURL(decodedUrl, target);
};

const isUrlEncoded = (url: string) => {
  try {
    // If the string is already encoded, decoding and re-encoding it will return the same string
    return (
      url !== decodeURIComponent(url) &&
      url === encodeURIComponent(decodeURIComponent(url))
    );
  } catch (e) {
    // If decoding fails, it's likely not a valid URL-encoded string
    return false;
  }
};

const addQueryParam = (urlString: string, key: string, value: string) => {
  // Split URL into parts (before and after hash)
  const [urlBeforeHash, hash = ''] = urlString.split('#');

  // Split the URL (before hash) into path and query
  const urlParts = urlBeforeHash.split('?');
  const path = urlParts[0];
  const queryString = urlParts.length > 1 ? urlParts[1] : '';

  // Create URLSearchParams object from the query string
  const searchParams = new URLSearchParams(queryString);

  // Add or update the parameter
  searchParams.set(key, value);

  // Get the new query string
  const newQueryString = searchParams.toString();

  // Return the full URL with the new parameter and hash (if any)
  return (
    path +
    (newQueryString ? `?${newQueryString}` : '') +
    (hash ? `#${hash}` : '')
  );
};

export const stripTextEncoding = (text: string): string => {
  if (!text) return '';

  // Normalize unicode (convert composed characters to their canonical form)
  let cleanText = text.normalize('NFC');

  // Replace zero-width spaces and other invisible characters
  cleanText = cleanText.replace(/[\u200B-\u200D\uFEFF\u2060-\u206F]/g, '');

  // Replace bidirectional formatting characters (including 8236/U+202C)
  cleanText = cleanText.replace(
    /[\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/g,
    '',
  );

  // Replace non-printable and control characters
  // eslint-disable-next-line no-control-regex
  cleanText = cleanText.replace(/[\x00-\x1F\x7F-\x9F]/g, '');

  // Replace variation selectors
  cleanText = cleanText.replace(/[\uFE00-\uFE0F]/g, '');

  // Replace other common invisible characters
  cleanText = cleanText.replace(
    /[\u180B-\u180E\u200C\u2028-\u202F\u205F\u3000]/g,
    ' ',
  );

  // Trim whitespace
  cleanText = cleanText.trim();

  return cleanText;
};

/**
 *  Adding this function to help visualize encoded strings
 *  This will be useful if the error ever happens again
 */
export const visualizeString = (str: string): string => {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    const codePoint = str.codePointAt(i);
    result += `Position ${i}: '${char}' (U+${codePoint
      ?.toString(16)
      .padStart(4, '0')})`;

    // If this is a surrogate pair, skip the next code unit
    if (codePoint && codePoint > 0xffff) {
      i++;
    }

    result += '\n';
  }
  return result;
};
